import { useCallback, useEffect, useRef, useState } from "react";
import {
  changeCheckBeatMs,
  notifyShowMoved,
  subscribeNudgeStore,
} from "common-fe/changeNudge";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #3559 -- every Score Admin timer screen runs a CHANGE CHECK and reloads only
// what moved (docs/adr/0003, GLOSSARY "Live screens"). Same design as
// announcer-fe's useShowChangeCheck (#3557), reshaped for a shell where several
// screens and sub-hooks are mounted at once.
//
// ONE cheap ask per beat, GET /show/:id/changes (score-api #3558): a set of
// counters (total, showVersion, one per class `c#`, one per ring `r#`). Each
// screen hands this hook the counters it depends on and gets back "ticks"
// (numbers that go up). A data hook runs its reload in an effect keyed on its
// tick (useOnTick below), so it re-reads exactly when its tick moves:
//
//   classTick      showVersion moved, or the counter of `classId` moved
//   ringTick       showVersion moved, or the counter of `ring` moved
//   showTick       showVersion moved (judge roster, show row)
//   wholeShowTick  the show-wide `total` moved (any write anywhere), or showVersion
//
// SHARED CHECK. The check itself is a module-level feed per show, reference
// counted: however many hooks and screens are mounted for a show (nav badges,
// the page, its sub-hooks), the beat is ONE request, not one per hook. Each hook
// compares successive answers against its own scope, so a hook watching class A
// does not fire for class B.
//
// BACKSTOP. Every hook also bumps ALL its ticks on its own timer (backstopMs,
// default 60 s, a screen may pick another), so a counter the signal does not
// cover can never freeze a screen for longer than that.
//
// First answer after a hook mounts only ADOPTS the baseline: the screen already
// loaded its own data on mount, re-reading it would double the cold start.
// A FAILED check changes nothing (no tick, no baseline); the next beat asks
// again. The screen keeps what it has.
// A move is owed ONE more tick on the next beat (the scribe #3252 pattern): the
// reads that follow a counter move can be eventually consistent and the counters
// are bumped by a separate async stream, so the first read may predate the write.
//
// NUDGES (card #3562, docs/adr/0003). `checkNow()` runs a check right now, and a
// hook takes an outside trigger too (`checkNowSignal`, any value that changes).
// The staff WebSocket (common-fe changeNudge, mounted once per app) also drives
// the shared feed through a store, with no per-call-site wiring: a NUDGE runs the
// feed's check at once; while the socket is HEALTHY the beat slows to 30 s (the
// nudge is the fast path, the check only backstops it); when it drops the beat is
// the server's pollMs again and a check runs right then. A check that sees the
// show move tells the store, so a socket released by Instant updates OFF can ask
// again whether it is ON now.
export const DEFAULT_CHANGE_POLL_MS = 5000;
export const MIN_CHANGE_POLL_MS = 1000;
export const BACKSTOP_REFRESH_MS = 60 * 1000;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function normalizePollMs(pollMs) {
  const n = Number(pollMs);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_CHANGE_POLL_MS;
  return Math.max(MIN_CHANGE_POLL_MS, Math.floor(n));
}

const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

const feeds = new Map();

function createFeed(showId) {
  const feed = {
    showId,
    // onAnswer -> that subscriber's fetcher. The shared check runs through a LIVE
    // subscriber's client (the newest still mounted), never one whose hook has
    // unmounted (its token / org header would be stale after a renew or org switch).
    subs: new Map(),
    last: null,
    inflight: null,
    timer: null,
    beatMs: null,
    pollMs: DEFAULT_CHANGE_POLL_MS,
    disposed: false,
  };

  const clear = () => {
    if (feed.timer != null) clearInterval(feed.timer);
    feed.timer = null;
    feed.beatMs = null;
  };

  feed.check = () => {
    const fetcher = [...feed.subs.values()].pop();
    if (feed.disposed || !fetcher) return Promise.resolve();
    if (feed.inflight) return feed.inflight;
    const run = (async () => {
      let answer;
      try {
        answer = await fetcher(feed.showId);
      } catch {
        return; // a failed check changes nothing
      }
      if (feed.disposed) return;
      feed.pollMs = normalizePollMs(answer?.pollMs);
      const next = {
        total: num(answer?.total),
        showVersion: num(answer?.showVersion),
        classes: answer?.classes ?? {},
        rings: answer?.rings ?? {},
      };
      const prev = feed.last;
      feed.last = next;
      if (prev && (next.total !== prev.total || next.showVersion !== prev.showVersion)) {
        notifyShowMoved(feed.showId);
      }
      for (const sub of [...feed.subs.keys()]) sub(prev, next);
    })();
    feed.inflight = run;
    run.finally(() => {
      if (feed.inflight === run) feed.inflight = null;
    });
    return run;
  };

  // setInterval, not a chained setTimeout, with an in-flight guard against
  // overlap. The beat is rebuilt when the server recommends a different pollMs.
  const schedule = () => {
    clear();
    if (feed.disposed || isHidden() || feed.subs.size === 0) return;
    feed.beatMs = changeCheckBeatMs(feed.showId, feed.pollMs);
    feed.timer = setInterval(async () => {
      await feed.check();
      if (
        !feed.disposed &&
        feed.timer != null &&
        feed.beatMs !== changeCheckBeatMs(feed.showId, feed.pollMs)
      ) {
        schedule();
      }
    }, feed.beatMs);
  };
  const onVisibility = async () => {
    if (isHidden()) {
      clear();
      return;
    }
    clear();
    await feed.check();
    schedule();
  };

  // A nudge that lands while a check is already running must not be absorbed by
  // it: that check may have read the server BEFORE the write the nudge announces.
  // Let it finish, then ask once more (concurrent nudges coalesce into that one).
  const checkAfterNudge = async () => {
    const pending = feed.inflight;
    if (pending) await pending;
    if (!feed.disposed) await feed.check();
  };

  let unsubscribeNudges = null;
  feed.start = () => {
    if (!isHidden()) feed.check().then(schedule);
    document.addEventListener("visibilitychange", onVisibility);
    unsubscribeNudges = subscribeNudgeStore(feed.showId, async (event) => {
      if (feed.disposed || isHidden()) return;
      if (event.kind === "nudge") {
        await checkAfterNudge();
      } else if (event.healthy) {
        schedule(); // healthy: slow the beat to 30 s
      } else {
        // down: the normal rate again, and catch up on what the socket may have missed
        schedule();
        await checkAfterNudge();
      }
    });
  };
  feed.stop = () => {
    feed.disposed = true;
    unsubscribeNudges?.();
    unsubscribeNudges = null;
    clear();
    document.removeEventListener("visibilitychange", onVisibility);
  };
  return feed;
}

function subscribe(showId, fetcher, onAnswer) {
  let feed = feeds.get(showId);
  if (!feed) {
    feed = createFeed(showId);
    feeds.set(showId, feed);
    feed.subs.set(onAnswer, fetcher);
    feed.start();
  } else {
    feed.subs.set(onAnswer, fetcher);
  }
  return {
    feed,
    unsubscribe() {
      feed.subs.delete(onAnswer);
      if (feed.subs.size === 0) {
        feed.stop();
        if (feeds.get(showId) === feed) feeds.delete(showId);
      }
    },
  };
}

const EMPTY_TICKS = { class: 0, ring: 0, show: 0, whole: 0 };
const NONE_OWED = { class: false, ring: false, show: false, whole: false };

export function useShowChangeCheck(
  showId,
  { classId, ring, enabled = true, backstopMs = BACKSTOP_REFRESH_MS, checkNowSignal } = {},
) {
  const api = useScoreApi();
  const [ticks, setTicks] = useState(EMPTY_TICKS);

  const scopeRef = useRef({ classId, ring });
  scopeRef.current = { classId, ring };
  const feedRef = useRef(null);

  // This hook's own newest client (a silent token renew rebuilds `api`); the
  // shared check uses the newest LIVE subscriber's.
  const fetcherRef = useRef(null);
  fetcherRef.current = (id) => api.shows.getShowChanges(id).then(unwrap);

  const bump = useCallback((which) => {
    setTicks((prev) => {
      const next = { ...prev };
      for (const k of which) next[k] += 1;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    // The backstop runs even with no show (the org-wide Review Queue badge has
    // nothing else to ask); the change check needs a show to ask about.
    const backstop = setInterval(() => {
      if (isHidden()) return;
      bump(["class", "ring", "show", "whole"]);
    }, backstopMs);
    let sub = null;
    if (showId) {
      let owed = { ...NONE_OWED };
      const onAnswer = (prev, next) => {
        if (!prev) return; // first answer: adopt, read nothing
        const { classId: c, ring: r } = scopeRef.current;
        const showMoved = next.showVersion !== prev.showVersion;
        const moved = {
          class: showMoved || (c != null && num(next.classes[c]) !== num(prev.classes[c])),
          ring: showMoved || (r != null && num(next.rings[r]) !== num(prev.rings[r])),
          show: showMoved,
          whole: showMoved || next.total !== prev.total,
        };
        const fire = Object.keys(moved).filter((k) => moved[k] || owed[k]);
        owed = moved; // a fresh move is owed one more read; an owed one settles
        if (fire.length) bump(fire);
      };
      sub = subscribe(showId, (id) => fetcherRef.current(id), onAnswer);
      feedRef.current = sub.feed;
    }
    return () => {
      clearInterval(backstop);
      if (sub) {
        sub.unsubscribe();
        if (feedRef.current === sub.feed) feedRef.current = null;
      }
    };
  }, [enabled, showId, backstopMs, bump]);

  const checkNow = useCallback(() => feedRef.current?.check() ?? Promise.resolve(), []);

  // An outside trigger (card 3562): any change in checkNowSignal asks right now.
  const firstSignalRef = useRef(true);
  useEffect(() => {
    if (firstSignalRef.current) {
      firstSignalRef.current = false;
      return;
    }
    checkNow();
  }, [checkNowSignal, checkNow]);

  return {
    classTick: ticks.class,
    ringTick: ticks.ring,
    showTick: ticks.show,
    wholeShowTick: ticks.whole,
    checkNow,
  };
}

// Run `fn` whenever `tick` goes up. Skips the value the hook mounted with (the
// screen already loaded on mount). `fn` may change identity freely. Ticks only
// ever go up (a show change keeps them; the new show's first answer is adopted
// without a tick), so no reload is lost to a reset landing with a bump.
export function useOnTick(tick, fn) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const seenRef = useRef(tick);
  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = tick;
    // Only a tick that WENT UP reloads.
    if (tick > seen) fnRef.current();
  }, [tick]);
}

// Test seam: drop every shared feed (a spec that leaves one mounted).
export function resetShowChangeFeeds() {
  for (const feed of feeds.values()) feed.stop();
  feeds.clear();
}

export default useShowChangeCheck;
